// Which GPU draws the game, and whether it is a weak one: software renderers,
// old integrated chips and small texture limits get the "low" graphics preset
// suggested on the first start.

export interface GpuInfo {
  renderer: string;
  maxTextureSize: number;
  /** Why the GPU looks weak (empty: it does not). */
  weakReasons: string[];
}

/** Software rasterizers and old / low-end integrated GPUs. */
const WEAK_RENDERERS = /swiftshader|llvmpipe|softpipe|software|microsoft basic render|mesa offscreen|mali-[4t]|adreno \(tm\) [2-5]\d\d|powervr|intel\(r\) (hd graphics( [2-5]\d{2,3})?|gma)\b|intel.*(sandybridge|ivybridge|haswell)/i;

export function classifyGpu(renderer: string, maxTextureSize: number, deviceMemory?: number): GpuInfo {
  const weakReasons: string[] = [];
  if (WEAK_RENDERERS.test(renderer)) weakReasons.push(`slow GPU (${renderer})`);
  if (maxTextureSize < 8192) weakReasons.push(`textures up to ${maxTextureSize} px`);
  if (deviceMemory !== undefined && deviceMemory <= 2) weakReasons.push(`${deviceMemory} GB memory`);
  return { renderer, maxTextureSize, weakReasons };
}

/** The GPU of a WebGL context. */
export function gpuInfo(gl: WebGLRenderingContext | WebGL2RenderingContext): GpuInfo {
  const ext = gl.getExtension('WEBGL_debug_renderer_info');
  const renderer = String((ext ? gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER)) ?? 'unknown');
  const memory = typeof navigator !== 'undefined' ? (navigator as Navigator & { deviceMemory?: number }).deviceMemory : undefined;
  return classifyGpu(renderer, Number(gl.getParameter(gl.MAX_TEXTURE_SIZE)) || 0, memory);
}
